<template>
  <div
    class="sidebar sidebar--compact"
    :data-background-color="backgroundColor"
    :data-active-color="activeColor"
  >
    <div class="sidebar-wrapper sidebar-wrapper-clipr-theme" id="style-3">
      <div class="sidebar-content">
        <div class="logo">
          <router-link to="/create-clips" class="simple-text sidebar-logo-link">
            <img src="/favicon.svg" alt="" class="logo-icon" />
            <span class="sidebar-logo-title">Clipr</span>
          </router-link>
        </div>
        <div v-if="impersonation" class="sidebar-impersonation">
          <div
            class="sidebar-impersonation__badge"
            :title="tt('navbar.impersonationActive', 'Impersonation active')"
          >
            {{ tt("navbar.impersonating", "Impersonating") }}: {{ impersonationLabel }}
          </div>
          <button
            type="button"
            class="sidebar-impersonation__stop"
            :title="tt('navbar.stopImpersonating', 'Stop impersonating')"
            @click="stopImpersonation"
          >
            <i class="fa-solid fa-user-slash" aria-hidden="true"></i>
            {{ tt("common.stop", "Stop") }}
          </button>
        </div>
        <ul class="nav sidebar-nav">
          <sidebar-link
            v-for="link in mainNavLinks"
            :key="link.path"
            :to="link.path"
            :name="link.name"
            :icon="link.icon"
            :same-route-reset-event="link.sameRouteResetEvent || null"
          />
          <sidebar-admin-group
            v-if="adminNavLinks.length"
            :links="adminNavLinks"
            :label="tt('nav.admin', 'Admin')"
          />
        </ul>
      </div>
      <div class="sidebar-footer">
        <a
          class="sidebar-whatsapp-menu"
          :href="whatsappHref"
          target="_blank"
          rel="noopener noreferrer"
          @click="closeMobileSidebar"
        >
          <i class="fa-brands fa-whatsapp" aria-hidden="true"></i>
          <span>{{ tt("app.whatsapp.title", "Contact us on WhatsApp") }}</span>
        </a>
        <button
          type="button"
          class="sidebar-suggestions-trigger sidebar-suggestions-footer-button"
          @click="openSuggestionsModal"
        >
          <i class="fa-regular fa-lightbulb" aria-hidden="true"></i>
          <span>{{ tt("nav.suggestions", "Sugerencias") }}</span>
        </button>
        <sidebar-profile-menu />
      </div>
    </div>
    <body-portal>
    <div
      v-if="showSuggestionsModal"
      class="sidebar-suggestions-modal-backdrop"
      @click.self="closeSuggestionsModal"
    >
      <section
        class="sidebar-suggestions-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="suggestions-modal-title"
      >
        <header class="sidebar-suggestions-modal__header">
          <div>
            <p>{{ tt("suggestions.eyebrow", "TU OPINIÓN") }}</p>
            <h2 id="suggestions-modal-title">{{ tt("suggestions.title", "Comparte una sugerencia") }}</h2>
          </div>
          <button
            type="button"
            class="sidebar-suggestions-modal__close"
            :aria-label="tt('common.close', 'Cerrar')"
            @click="closeSuggestionsModal"
          >
            <i class="ti-close"></i>
          </button>
        </header>
        <p class="sidebar-suggestions-modal__intro">
          {{ tt("suggestions.intro", "Cuéntanos qué mejorarías en Clipr.") }}
        </p>
        <form @submit.prevent="submitSuggestion">
          <label class="sidebar-suggestions-modal__field">
            <span>{{ tt("suggestions.category", "Tipo de sugerencia") }}</span>
            <select v-model="suggestion.category" required>
              <option value="">{{ tt("suggestions.categoryPlaceholder", "Selecciona una opción") }}</option>
              <option value="idea">{{ tt("suggestions.idea", "Nueva idea") }}</option>
              <option value="improvement">{{ tt("suggestions.improvement", "Mejora") }}</option>
              <option value="problem">{{ tt("suggestions.problem", "Problema") }}</option>
              <option value="other">{{ tt("suggestions.other", "Otro") }}</option>
            </select>
          </label>
          <label class="sidebar-suggestions-modal__field">
            <span>{{ tt("suggestions.subject", "Asunto") }}</span>
            <input
              v-model.trim="suggestion.subject"
              type="text"
              :placeholder="tt('suggestions.subjectPlaceholder', 'Resume tu sugerencia')"
              maxlength="120"
              required
            />
          </label>
          <label class="sidebar-suggestions-modal__field">
            <span>{{ tt("suggestions.description", "Descripción") }}</span>
            <textarea
              v-model.trim="suggestion.description"
              :placeholder="tt('suggestions.descriptionPlaceholder', 'Describe tu idea o problema con el mayor detalle posible')"
              rows="5"
              maxlength="1500"
              required
            ></textarea>
          </label>
          <p v-if="suggestionSubmitted" class="sidebar-suggestions-modal__notice">
            {{ tt("suggestions.successNotice", "Sugerencia enviada. Gracias por compartir tu opinión.") }}
          </p>
          <p v-if="suggestionError" class="sidebar-suggestions-modal__error">
            {{ suggestionError }}
          </p>
          <footer class="sidebar-suggestions-modal__footer">
            <button type="button" class="sidebar-suggestions-modal__cancel" @click="closeSuggestionsModal">
              {{ tt("common.cancel", "Cancelar") }}
            </button>
            <button type="submit" class="sidebar-suggestions-modal__submit" :disabled="suggestionSubmitting">
              {{ suggestionSubmitting ? tt("suggestions.submitting", "Enviando...") : tt("suggestions.submit", "Enviar sugerencia") }}
            </button>
          </footer>
        </form>
      </section>
    </div>
    </body-portal>
  </div>
</template>
<script>
import SidebarLink from "./SidebarLink";
import SidebarAdminGroup from "./SidebarAdminGroup";
import SidebarProfileMenu from "./SidebarProfileMenu";
import { clearImpersonation, getImpersonation, IMPERSONATION_EVENT } from "@/utils/impersonation";
import { useCurrentUserStore } from "@/stores/currentUser";
import { createSuggestion } from "@/api/suggestions";
import { OPEN_SUGGESTIONS_MODAL_EVENT } from "@/config/navRoutes";
import { supabase } from "@/supabase";

export default {
  props: {
    title: {
      type: String,
      default: "[...] Clipr",
    },
    backgroundColor: {
      type: String,
      default: "black",
      validator: (value) => {
        let acceptedValues = ["white", "black", "darkblue"];
        return acceptedValues.indexOf(value) !== -1;
      },
    },
    activeColor: {
      type: String,
      default: "success",
      validator: (value) => {
        let acceptedValues = [
          "primary",
          "info",
          "success",
          "warning",
          "danger",
        ];
        return acceptedValues.indexOf(value) !== -1;
      },
    },
    sidebarLinks: {
      type: Array,
      default: () => [],
    },
    navLinks: {
      type: Array,
      default: () => [],
    },
    autoClose: {
      type: Boolean,
      default: true,
    },
  },
  provide() {
    return {
      autoClose: this.autoClose,
      addLink: this.addLink,
      removeLink: this.removeLink,
    };
  },
  components: {
    SidebarLink,
    SidebarAdminGroup,
    SidebarProfileMenu,
  },
  data() {
    return {
      activeLinkIndex: 0,
      windowWidth: 0,
      isWindows: false,
      hasAutoHeight: false,
      links: [],
      impersonation: getImpersonation(),
      showSuggestionsModal: false,
      suggestionSubmitted: false,
      suggestionSubmitting: false,
      suggestionError: "",
      suggestion: {
        category: "",
        subject: "",
        description: "",
      },
    };
  },
  computed: {
    currentUserStore() {
      return useCurrentUserStore();
    },
    impersonationLabel() {
      const imp = this.impersonation || {};
      const name = (imp.username || "").trim();
      const email = (imp.email || "").trim();
      if (name && email) return `${name} (${email})`;
      if (name) return name;
      if (email) return email;
      return imp.userId || "unknown";
    },
    resolvedNavLinks() {
      return this.navLinks.length ? this.navLinks : this.sidebarLinks;
    },
    hasAdminAccess() {
      return this.currentUserStore.hasAdminAccess;
    },
    mainNavLinks() {
      return this.resolvedNavLinks.filter(
        (link) => !link.hidden && !link.adminOnly && (!link.userOnly || !this.hasAdminAccess)
      );
    },
    adminNavLinks() {
      if (!this.hasAdminAccess) return [];
      return this.resolvedNavLinks.filter((link) => link.adminOnly);
    },
    whatsappHref() {
      const userLanguage =
        this.currentUserStore?.profile?.user_default_lenguage ||
        this.currentUserStore?.ensuredProfile?.user_default_lenguage ||
        this.currentUserStore?.locale ||
        "es";
      const number = userLanguage === "en" ? "528112557887" : "525577169082";
      return `https://wa.me/${number}?text=${encodeURIComponent(this.tt("app.whatsapp.prefilledMessage", ""))}`;
    },
  },
  beforeDestroy() {
    window.removeEventListener("storage", this.syncImpersonation);
    window.removeEventListener(IMPERSONATION_EVENT, this.syncImpersonation);
    window.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener(OPEN_SUGGESTIONS_MODAL_EVENT, this.openSuggestionsModal);
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    syncImpersonation() {
      this.impersonation = getImpersonation();
    },
    openSuggestionsModal() {
      this.suggestionSubmitted = false;
      this.suggestionError = "";
      this.showSuggestionsModal = true;
    },
    closeSuggestionsModal() {
      this.showSuggestionsModal = false;
    },
    onKeydown(event) {
      if (event.key === "Escape" && this.showSuggestionsModal) {
        this.closeSuggestionsModal();
      }
    },
    closeMobileSidebar() {
      if (this.$sidebar.showSidebar) {
        this.$sidebar.displaySidebar(false);
      }
    },
    async submitSuggestion() {
      this.suggestionSubmitting = true;
      this.suggestionSubmitted = false;
      this.suggestionError = "";
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        if (error) throw error;
        if (!session) {
          throw new Error(this.tt("suggestions.authRequired", "Debes iniciar sesión para enviar una sugerencia."));
        }

        await createSuggestion(session, this.suggestion);
        this.suggestionSubmitted = true;
        this.suggestion = {
          category: "",
          subject: "",
          description: "",
        };
      } catch (error) {
        this.suggestionError = error.message;
      } finally {
        this.suggestionSubmitting = false;
      }
    },
    async stopImpersonation() {
      clearImpersonation();
      this.impersonation = null;
      await this.currentUserStore.refresh();
      this.$notify({
        message: this.tt("navbar.impersonationEnded", "Impersonation ended."),
        type: "info",
      });
      this.$router.push("/users");
    },
    findActiveLink() {
      // Default to 0; updated once we find the active link
      let nextIndex = 0;
      this.links.forEach((link, index) => {
        if (link.isActive()) {
          nextIndex = index;
        }
      });
      this.activeLinkIndex = nextIndex;
    },
    addLink(link) {
      // With nested <template v-if> slot content, indexOf can be -1.
      // Fall back to appending so ordering doesn't get corrupted.
      const slotIndex = this.$slots?.links ? this.$slots.links.indexOf(link.$vnode) : -1;
      const insertAt = slotIndex >= 0 ? slotIndex : this.links.length;
      this.links.splice(insertAt, 0, link);

      // Wait until VueRouter updates active classes, then recompute
      this.$nextTick(() => this.findActiveLink());
    },
    removeLink(link) {
      const index = this.links.indexOf(link);
      if (index > -1) {
        this.links.splice(index, 1);
      }

      // If links changed, wait for DOM to settle and recompute
      this.$nextTick(() => this.findActiveLink());
    },
  },
  async mounted() {
    window.addEventListener("storage", this.syncImpersonation);
    window.addEventListener(IMPERSONATION_EVENT, this.syncImpersonation);
    window.addEventListener("keydown", this.onKeydown);
    window.addEventListener(OPEN_SUGGESTIONS_MODAL_EVENT, this.openSuggestionsModal);
    // Compute after DOM updates so we read the correct "active" class
    this.$watch(
      () => this.$route.fullPath,
      () => this.$nextTick(() => this.findActiveLink()),
      { immediate: true }
    );
  },
};
</script>
<style>
:root {
  --clipr-sidebar-compact-width: 112px;
  --clipr-sidebar-nav-item-height: 68px;
  --clipr-sidebar-nav-link-padding-y: 8px;
  --clipr-sidebar-nav-link-padding-x: 6px;
  --clipr-sidebar-nav-icon-size: 1.15rem;
  --clipr-sidebar-nav-label-size: 0.64rem;
  --clipr-sidebar-nav-label-line-height: 1.25;
  --clipr-sidebar-icon-well-size: 34px;
  --clipr-sidebar-font: "Inter", "Segoe UI", system-ui, -apple-system, sans-serif;
  --clipr-sidebar-footer-card-padding: 8px 6px;
  --clipr-sidebar-footer-card-gap: 4px;
  --clipr-sidebar-footer-card-min-height: 78px;
  --clipr-sidebar-footer-icon-size: 32px;
}

.wrapper .sidebar.sidebar--compact,
.wrapper .sidebar.sidebar--compact .sidebar-wrapper {
  width: var(--clipr-sidebar-compact-width) !important;
  z-index: 1030 !important;
}

.wrapper .sidebar.sidebar--compact ~ .main-panel {
  width: calc(100% - var(--clipr-sidebar-compact-width)) !important;
}

.sidebar-wrapper {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.sidebar-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.wrapper .sidebar.sidebar--compact .sidebar-wrapper .logo,
.sidebar-wrapper .logo {
  flex-shrink: 0;
  margin: 0 !important;
  padding: 14px 0;
  text-align: center;
  border-bottom: 1px solid rgba(196, 181, 253, 0.18) !important;
}

.sidebar-logo-link {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.sidebar-logo-link:hover {
  opacity: 0.92;
}

.sidebar-logo-link .logo-icon {
  filter: drop-shadow(0 0 10px rgba(168, 85, 247, 0.35));
  transition: filter 0.25s ease, transform 0.25s ease;
}

.sidebar-logo-link:hover .logo-icon {
  filter: drop-shadow(0 0 14px rgba(168, 85, 247, 0.5));
  transform: scale(1.04);
}

.sidebar-logo-title {
  font-family: var(--clipr-sidebar-font);
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  line-height: 1;
  text-transform: uppercase;
  background: linear-gradient(135deg, #f5f3ff 0%, #c4b5fd 55%, #a855f7 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.sidebar-impersonation {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 6px 8px;
  padding: 8px;
  border-radius: var(--clipr-radius-md);
  background: rgba(168, 85, 247, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.sidebar-impersonation__badge {
  font-family: var(--clipr-sidebar-font);
  font-size: 9px;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: 0.02em;
  color: var(--clipr-purple-light);
  word-break: break-word;
}

.sidebar-impersonation__stop {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 5px 6px;
  border: none;
  border-radius: var(--clipr-radius-sm);
  background: var(--clipr-purple-gradient);
  color: #fff;
  font-family: var(--clipr-sidebar-font);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(168, 85, 247, 0.25);
  transition: box-shadow 0.2s ease, transform 0.15s ease;
}

.sidebar-impersonation__stop:hover {
  box-shadow: 0 6px 16px rgba(168, 85, 247, 0.35);
  transform: translateY(-1px);
}

.sidebar-impersonation__stop i {
  font-size: 0.65rem;
}

.sidebar-footer {
  flex-shrink: 0;
  position: relative;
  overflow: visible;
  z-index: 20;
  padding: 4px 0 14px;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(168, 85, 247, 0.03) 40%,
    transparent 100%
  );
}

.sidebar-wrapper .nav.sidebar-nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  align-content: flex-start;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  margin: 0;
  padding: 0 4px 8px;
  scrollbar-width: thin;
  scrollbar-color: var(--clipr-scrollbar-thumb-firefox) var(--clipr-scrollbar-track);
}

.sidebar-wrapper .nav::-webkit-scrollbar {
  width: var(--clipr-scrollbar-size);
}

.sidebar-wrapper .nav::-webkit-scrollbar-track {
  background: var(--clipr-scrollbar-track);
  border-radius: 999px;
}

.sidebar-wrapper .nav::-webkit-scrollbar-thumb {
  background: var(--clipr-scrollbar-thumb);
  border-radius: 999px;
  border: 2px solid var(--clipr-scrollbar-thumb-border);
}

.sidebar-footer-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--clipr-sidebar-footer-card-gap);
  width: 100%;
  min-width: 0;
  min-height: var(--clipr-sidebar-footer-card-min-height);
  padding: var(--clipr-sidebar-footer-card-padding);
  border-radius: var(--clipr-radius-md);
  position: relative;
  box-sizing: border-box;
  text-align: center;
  background: linear-gradient(
    135deg,
    var(--clipr-purple-overlay-light) 0%,
    rgba(79, 70, 229, 0.08) 45%,
    var(--clipr-purple-overlay-medium) 100%
  );
  border: 1.5px solid var(--clipr-purple-overlay-border);
  box-shadow:
    0 0 0 1px rgba(168, 85, 247, 0.12),
    0 4px 16px rgba(0, 0, 0, 0.25);
  transition: box-shadow 0.2s ease;
}

.sidebar-footer-card--profile {
  cursor: pointer;
}

.sidebar-footer-card--profile:hover:not(.sidebar-footer-card--unlimited) {
  box-shadow:
    0 0 0 1px rgba(168, 85, 247, 0.2),
    0 6px 20px rgba(0, 0, 0, 0.3),
    0 0 28px rgba(168, 85, 247, 0.22);
}

.sidebar-footer-card--unlimited {
  border-color: transparent;
}

.sidebar-footer-card--unlimited::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1.5px;
  background: linear-gradient(135deg, #22d3ee 0%, #8b5cf6 55%, #a855f7 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

.sidebar-footer-card--unlimited:hover {
  box-shadow:
    0 0 0 1px rgba(168, 85, 247, 0.28),
    0 8px 24px rgba(0, 0, 0, 0.32),
    0 0 32px rgba(168, 85, 247, 0.35);
}

.sidebar-footer-card__avatar {
  width: var(--clipr-sidebar-footer-icon-size);
  height: var(--clipr-sidebar-footer-icon-size);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(160deg, rgba(168, 85, 247, 0.25) 0%, rgba(79, 70, 229, 0.18) 100%);
  border: 1px solid rgba(196, 181, 253, 0.45);
}

.sidebar-footer-card__avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.sidebar-footer-card__avatar-icon {
  font-size: 0.95rem;
  color: #c4b5fd;
}

.sidebar-footer-card__title {
  font-family: var(--clipr-sidebar-font);
  font-size: 0.58rem;
  font-weight: 600;
  line-height: 1.2;
  color: var(--clipr-text-primary);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-footer-card__subtitle {
  font-family: var(--clipr-sidebar-font);
  font-size: 0.5rem;
  font-weight: 600;
  line-height: 1.2;
  max-width: 100%;
  min-height: calc(0.5rem * 1.2);
}

.sidebar-footer-card__subtitle--free {
  color: var(--clipr-text-secondary);
  font-size: 0.56rem;
}

.sidebar-footer-card__subtitle--unlimited {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: var(--clipr-purple-light);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.sidebar-footer-card__subtitle-icon {
  font-size: 0.5rem;
  color: #fbbf24;
  filter: drop-shadow(0 0 4px rgba(251, 191, 36, 0.4));
}

.logo-icon {
  width: 34px;
  height: 34px;
  display: block;
}

.sidebar-wrapper-clipr-theme {
  background: var(--clipr-bg-login-left);
  box-shadow: 6px 0 28px rgba(0, 0, 0, 0.18) !important;
}

.wrapper .sidebar.sidebar--compact .nav .nav-item {
  width: 100% !important;
  height: var(--clipr-sidebar-nav-item-height);
  min-height: var(--clipr-sidebar-nav-item-height);
  max-height: var(--clipr-sidebar-nav-item-height);
  margin: 0 !important;
  padding: 0;
  list-style: none;
  flex: 0 0 var(--clipr-sidebar-nav-item-height);
  box-sizing: border-box;
  display: flex;
  align-items: stretch;
}

.wrapper .sidebar.sidebar--compact .nav .nav-item > .nav-link,
.wrapper .sidebar.sidebar--compact .sidebar-admin-group__trigger {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  box-sizing: border-box;
  flex: 1 1 auto;
  width: 100%;
  height: var(--clipr-sidebar-nav-item-height);
  min-height: var(--clipr-sidebar-nav-item-height);
  max-height: var(--clipr-sidebar-nav-item-height);
  margin: 0 !important;
  padding: var(--clipr-sidebar-nav-link-padding-y) var(--clipr-sidebar-nav-link-padding-x) !important;
  border: none;
  border-radius: var(--clipr-radius-md);
  color: var(--clipr-text-tertiary);
  opacity: 1 !important;
  text-decoration: none;
  position: relative;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.wrapper .sidebar.sidebar--compact .nav .nav-item > .nav-link::before,
.wrapper .sidebar.sidebar--compact .sidebar-admin-group__trigger::before {
  content: "";
  position: absolute;
  left: 2px;
  top: 50%;
  transform: translateY(-50%) scaleY(0);
  width: 3px;
  height: 22px;
  border-radius: 999px;
  background: linear-gradient(180deg, #c084fc 0%, #a855f7 100%);
  box-shadow: 0 0 10px rgba(168, 85, 247, 0.5);
  transition: transform 0.2s ease;
}

.wrapper .sidebar.sidebar--compact .nav i,
.wrapper .sidebar.sidebar--compact .nav .sidebar-nav-link__icon {
  float: none !important;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 !important;
  width: var(--clipr-sidebar-icon-well-size) !important;
  height: 28px !important;
  font-size: var(--clipr-sidebar-nav-icon-size);
  line-height: 1 !important;
  text-align: center !important;
  flex-shrink: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  color: var(--clipr-text-secondary);
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.wrapper .sidebar.sidebar--compact .nav .sidebar-nav-link__label,
.wrapper .sidebar.sidebar--compact .nav p {
  float: none !important;
  margin: 0 !important;
  width: 100%;
  font-family: var(--clipr-sidebar-font);
  font-size: var(--clipr-sidebar-nav-label-size);
  font-weight: 500;
  letter-spacing: 0.03em;
  line-height: var(--clipr-sidebar-nav-label-line-height) !important;
  text-transform: none !important;
  text-align: center;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  max-height: calc(
    var(--clipr-sidebar-nav-label-size) * var(--clipr-sidebar-nav-label-line-height) * 2
  );
  word-break: keep-all;
  overflow-wrap: normal;
  hyphens: none;
}

.wrapper .sidebar.sidebar--compact .nav .nav-item > .nav-link:hover,
.wrapper .sidebar.sidebar--compact .sidebar-admin-group__trigger:hover {
  color: var(--clipr-text-primary);
  background: rgba(255, 255, 255, 0.04);
}

.wrapper .sidebar.sidebar--compact .nav .nav-item > .nav-link:hover .sidebar-nav-link__icon,
.wrapper .sidebar.sidebar--compact .sidebar-admin-group__trigger:hover .sidebar-nav-link__icon {
  background: rgba(168, 85, 247, 0.1);
  color: var(--clipr-purple-light);
}

.wrapper .sidebar.sidebar--compact .nav .nav-item.active > .nav-link,
.wrapper .sidebar.sidebar--compact .sidebar-admin-group.active > .sidebar-admin-group__trigger,
.wrapper .sidebar.sidebar--compact .sidebar-admin-group--open > .sidebar-admin-group__trigger {
  background: rgba(168, 85, 247, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  color: var(--clipr-text-primary);
}

.wrapper .sidebar.sidebar--compact .nav .nav-item.active > .nav-link::before,
.wrapper .sidebar.sidebar--compact .sidebar-admin-group.active > .sidebar-admin-group__trigger::before,
.wrapper .sidebar.sidebar--compact .sidebar-admin-group--open > .sidebar-admin-group__trigger::before {
  transform: translateY(-50%) scaleY(1);
}

.wrapper .sidebar.sidebar--compact .nav .nav-item.active > .nav-link .sidebar-nav-link__icon,
.wrapper .sidebar.sidebar--compact .sidebar-admin-group.active .sidebar-nav-link__icon,
.wrapper .sidebar.sidebar--compact .sidebar-admin-group--open .sidebar-nav-link__icon {
  background: rgba(168, 85, 247, 0.18);
  color: var(--clipr-purple-light);
  box-shadow: 0 0 14px rgba(168, 85, 247, 0.2);
}

.wrapper .sidebar.sidebar--compact .nav .nav-item.active > .nav-link .sidebar-nav-link__label,
.wrapper .sidebar.sidebar--compact .sidebar-admin-group.active .sidebar-nav-link__label,
.wrapper .sidebar.sidebar--compact .sidebar-admin-group--open .sidebar-nav-link__label {
  font-weight: 600;
  color: var(--clipr-text-primary);
}

.wrapper .sidebar.sidebar--compact .sidebar-profile {
  padding: 8px 6px 0;
  overflow: visible;
}

.sidebar-suggestions-trigger {
  appearance: none;
  -webkit-appearance: none;
  border: 0 !important;
  outline: 0;
  font: inherit;
  cursor: pointer;
}

.sidebar-suggestions-footer-button {
  width: calc(100% - 12px);
  min-height: 58px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 0 6px 8px;
  padding: 7px 6px;
  color: var(--clipr-text-tertiary);
  border-radius: var(--clipr-radius-md);
  background: transparent !important;
  font-family: var(--clipr-sidebar-font);
  font-size: var(--clipr-sidebar-nav-label-size);
  font-weight: 500;
}

.sidebar-suggestions-footer-button i {
  width: var(--clipr-sidebar-icon-well-size);
  height: 27px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--clipr-text-secondary);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  font-size: var(--clipr-sidebar-nav-icon-size);
}

.sidebar-suggestions-footer-button:hover,
.sidebar-suggestions-footer-button:focus-visible {
  color: var(--clipr-text-primary);
  background: rgba(255, 255, 255, 0.04) !important;
}

.sidebar-suggestions-footer-button:hover i,
.sidebar-suggestions-footer-button:focus-visible i {
  color: var(--clipr-purple-light);
  background: rgba(168, 85, 247, 0.1);
}

.sidebar-whatsapp-menu {
  display: none;
}

.wrapper .sidebar.sidebar--compact .nav .nav-item > .sidebar-suggestions-trigger {
  height: var(--clipr-sidebar-nav-item-height);
  min-height: var(--clipr-sidebar-nav-item-height);
  max-height: var(--clipr-sidebar-nav-item-height);
  color: var(--clipr-text-tertiary);
  background: transparent !important;
}

.wrapper .sidebar.sidebar--compact .nav .nav-item > .sidebar-suggestions-trigger:hover,
.wrapper .sidebar.sidebar--compact .nav .nav-item > .sidebar-suggestions-trigger:focus-visible {
  color: var(--clipr-text-primary);
  background: rgba(255, 255, 255, 0.04) !important;
}

.sidebar-suggestions-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(2, 6, 23, 0.76);
  backdrop-filter: blur(8px);
}

.sidebar-suggestions-modal {
  width: min(100%, 460px);
  padding: 24px;
  color: #f8fafc;
  border: 1px solid rgba(196, 181, 253, 0.35);
  border-radius: 18px;
  background:
    radial-gradient(circle at top right, rgba(168, 85, 247, 0.2), transparent 42%),
    #12131b;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
}

.sidebar-suggestions-modal__header,
.sidebar-suggestions-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.sidebar-suggestions-modal__header p {
  margin: 0 0 4px;
  color: #c4b5fd;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
}

.sidebar-suggestions-modal__header h2 {
  margin: 0;
  color: #fff;
  font-size: 20px;
}

.sidebar-suggestions-modal__close,
.sidebar-suggestions-modal__cancel,
.sidebar-suggestions-modal__submit {
  border: 0;
  cursor: pointer;
}

.sidebar-suggestions-modal__close {
  width: 34px;
  height: 34px;
  color: #cbd5e1;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.06);
}

.sidebar-suggestions-modal__intro {
  margin: 12px 0 20px;
  color: #94a3b8;
  font-size: 13px;
}

.sidebar-suggestions-modal__field {
  display: block;
  margin-bottom: 14px;
}

.sidebar-suggestions-modal__field span {
  display: block;
  margin-bottom: 6px;
  color: #e2e8f0;
  font-size: 12px;
  font-weight: 600;
}

.sidebar-suggestions-modal__field input,
.sidebar-suggestions-modal__field select,
.sidebar-suggestions-modal__field textarea {
  width: 100%;
  padding: 10px 12px;
  color: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 9px;
  outline: 0;
  background: rgba(15, 23, 42, 0.78);
  font: inherit;
}

.sidebar-suggestions-modal__field textarea {
  resize: vertical;
}

.sidebar-suggestions-modal__field input:focus,
.sidebar-suggestions-modal__field select:focus,
.sidebar-suggestions-modal__field textarea:focus {
  border-color: #a855f7;
  box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.16);
}

.sidebar-suggestions-modal__notice {
  margin: 0 0 16px;
  padding: 10px 12px;
  color: #bbf7d0;
  border: 1px solid rgba(74, 222, 128, 0.28);
  border-radius: 9px;
  background: rgba(22, 163, 74, 0.12);
  font-size: 12px;
}

.sidebar-suggestions-modal__error {
  margin: 0 0 16px;
  padding: 10px 12px;
  color: #fecaca;
  border: 1px solid rgba(248, 113, 113, 0.28);
  border-radius: 9px;
  background: rgba(220, 38, 38, 0.12);
  font-size: 12px;
}

.sidebar-suggestions-modal__footer {
  justify-content: flex-end;
  margin-top: 20px;
}

.sidebar-suggestions-modal__cancel,
.sidebar-suggestions-modal__submit {
  padding: 10px 14px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 700;
}

.sidebar-suggestions-modal__cancel {
  color: #cbd5e1;
  background: transparent;
}

.sidebar-suggestions-modal__submit {
  color: #fff;
  background: linear-gradient(135deg, #7e22ce, #a855f7);
}

.sidebar-suggestions-modal__submit:disabled {
  cursor: wait;
  opacity: 0.7;
}

@media (max-width: 991px) {
  .nav-open .wrapper::before {
    content: "";
    position: fixed;
    inset: 0;
    z-index: 1029;
    background: rgba(2, 6, 23, 0.58);
    pointer-events: none;
  }

  .wrapper .sidebar.sidebar--compact {
    --clipr-mobile-sidebar-width: min(320px, calc(100vw - 48px));
    width: var(--clipr-mobile-sidebar-width) !important;
    transform: translate3d(calc(-1 * var(--clipr-mobile-sidebar-width)), 0, 0) !important;
  }

  .wrapper .sidebar.sidebar--compact .sidebar-wrapper {
    width: var(--clipr-mobile-sidebar-width) !important;
  }

  .wrapper .sidebar.sidebar--compact ~ .main-panel,
  .nav-open .wrapper .sidebar.sidebar--compact ~ .main-panel {
    width: 100% !important;
    transform: none !important;
  }

  .nav-open .wrapper .sidebar.sidebar--compact {
    transform: translate3d(0, 0, 0) !important;
  }

  .wrapper .sidebar.sidebar--compact .sidebar-wrapper .logo {
    padding: 14px 18px;
    text-align: left;
  }

  .sidebar-logo-link {
    flex-direction: row;
  }

  .wrapper .sidebar.sidebar--compact .nav .nav-item {
    height: 56px;
    min-height: 56px;
    max-height: 56px;
    flex-basis: 56px;
  }

  .wrapper .sidebar.sidebar--compact .nav .nav-item > .nav-link,
  .wrapper .sidebar.sidebar--compact .sidebar-admin-group__trigger {
    flex-direction: row;
    justify-content: flex-start;
    gap: 12px;
    height: 56px;
    min-height: 56px;
    max-height: 56px;
    padding: 8px 16px !important;
  }

  .wrapper .sidebar.sidebar--compact .nav .nav-item.active > .nav-link::before {
    top: 50% !important;
    right: auto !important;
    margin-left: 0 !important;
    border: 0 !important;
  }

  .wrapper .sidebar.sidebar--compact .nav .nav-item.active > .nav-link::after {
    display: none !important;
  }

  .wrapper .sidebar.sidebar--compact .nav .sidebar-nav-link__label,
  .wrapper .sidebar.sidebar--compact .nav p {
    width: auto;
    max-height: none;
    overflow: visible;
    display: block;
    font-size: 0.875rem;
    line-height: 1.25 !important;
    text-align: left;
  }

  .sidebar-footer {
    padding: 8px 12px 16px;
  }

  .sidebar-whatsapp-menu {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 48px;
    padding: 10px 16px;
    color: #f8fafc;
    border: 1px solid rgba(37, 211, 102, 0.5);
    border-radius: var(--clipr-radius-md);
    background: rgba(37, 211, 102, 0.12);
    font-family: var(--clipr-sidebar-font);
    font-size: 0.875rem;
    font-weight: 600;
    text-decoration: none;
  }

  .sidebar-whatsapp-menu i {
    color: #25d366;
    font-size: 1.25rem;
  }

  .sidebar-suggestions-footer-button {
    width: 100%;
    min-height: 48px;
    flex-direction: row;
    justify-content: flex-start;
    gap: 12px;
    margin: 8px 0;
    padding: 10px 16px;
    font-size: 0.875rem;
  }

  .sidebar-suggestions-footer-button i {
    width: auto;
    height: auto;
    background: transparent;
  }

  .sidebar-suggestions-modal-backdrop {
    box-sizing: border-box;
    inset: auto;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100dvh;
    padding: 16px;
  }

  .sidebar-suggestions-modal {
    box-sizing: border-box;
    width: min(100%, 460px);
    max-height: calc(100dvh - 32px);
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 20px;
  }
}
</style>
